/**
 * Registration form + receipt viewer (Formulario | Recibo), with print and "Guardar PDF".
 * - variant "page": the public /p/:token page — the page itself is the printable document
 * - variant "portal": full-screen overlay for use inside the admin app (hides the app while printing)
 * The receipt is always the letter layout so form and receipt print as a matched set.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, FileDown, FileText, Loader2, Printer, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getPublicReceipt } from '@/features/receipts/data/receiptsRepository';
import { LetterReceiptPrint } from '@/features/receipts/components/LetterReceiptPrint';
import { publicReceiptToPrintData, formatReceiptNumber } from '@/features/receipts/types';
import { publicReceiptUrl } from '@/features/receipts/utils/publicReceiptUrl';
import { PrintPortal } from '../../print/PrintPortal';
import { exportSheetToPdf, pdfFileName } from '../../print/exportPdf';
import { publicFormUrl } from '../publicUrls';
import type { PublicRegistration } from '../types';
import { RegistrationFormPrint } from './RegistrationFormPrint';
import { useSignatureImage } from './useSignatureImage';
import { receiptUnavailableReason } from './receiptAvailability';

type DocTab = 'form' | 'receipt';

interface RegistrationDocumentViewerProps {
  registration: PublicRegistration;
  token: string;
  variant: 'page' | 'portal';
  /** Open on the receipt tab with this receipt number selected (shared link) */
  initialReceiptNumber?: number;
  /** portal only */
  onClose?: () => void;
}

export function RegistrationDocumentViewer({ registration, token, variant, initialReceiptNumber, onClose }: RegistrationDocumentViewerProps) {
  const receipts = useMemo(() => registration.receipts ?? [], [registration.receipts]);
  const reason = receiptUnavailableReason(registration);

  const signatureUrl = useSignatureImage(token, !!registration.form_signed_at);
  const initialIdx = initialReceiptNumber ? receipts.findIndex((r) => r.number === initialReceiptNumber) : -1;
  const [tab, setTab] = useState<DocTab>(initialIdx >= 0 ? 'receipt' : 'form');
  const [receiptIdx, setReceiptIdx] = useState(Math.max(initialIdx, 0));
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  // Letter sheet is 816px wide; shrink to fit narrow screens (16px gutters)
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const zoom = Math.min(1, (viewportWidth - 32) / 816);

  const selected = receipts[Math.min(receiptIdx, Math.max(receipts.length - 1, 0))];
  const { data: receiptData, error: receiptError } = useQuery({
    queryKey: ['public_receipt', selected?.public_token],
    queryFn: () => getPublicReceipt(selected.public_token),
    enabled: tab === 'receipt' && !!selected,
    retry: false,
  });
  const receiptPrint = receiptData && selected
    ? publicReceiptToPrintData(receiptData, publicReceiptUrl(selected.public_token))
    : null;

  const showingReceipt = tab === 'receipt' && !!selected;
  const sheet = showingReceipt ? (
    receiptPrint ? (
      <LetterReceiptPrint data={receiptPrint} />
    ) : (
      <div
        className="flex items-center justify-center bg-white text-sm text-gray-500"
        style={{ width: '8.5in', height: '10.96in' }}
      >
        {receiptError ? 'No se pudo cargar el recibo.' : <Loader2 className="size-6 animate-spin" />}
      </div>
    )
  ) : (
    <RegistrationFormPrint registration={registration} qrUrl={publicFormUrl(token)} signatureUrl={signatureUrl} />
  );
  const canExport = !showingReceipt || !!receiptPrint;

  const fileName = showingReceipt && selected
    ? pdfFileName('Recibo', formatReceiptNumber(selected.number))
    : pdfFileName('Formulario', registration.event.code, registration.form.dni);

  // Renders an unscaled copy off-screen (CSS zoom confuses html2canvas), captures it, then removes it
  useEffect(() => {
    if (!exporting) return;
    let cancelled = false;
    (async () => {
      try {
        if (!exportRef.current) return;
        await exportSheetToPdf(exportRef.current, fileName);
      } catch (err) {
        console.error('PDF export failed', err);
        if (!cancelled) setExportError(true);
      } finally {
        if (!cancelled) setExporting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exporting, fileName]);

  const startExport = () => {
    setExportError(false);
    setExporting(true);
  };

  // Real tabs (underline on the active one), not pill buttons
  const tabButton = (value: DocTab, label: string, Icon: typeof FileText, disabled = false) => (
    <button
      key={value}
      type="button"
      role="tab"
      aria-selected={tab === value}
      disabled={disabled}
      title={disabled ? (reason ?? undefined) : undefined}
      onClick={() => setTab(value)}
      className={`-mb-px inline-flex flex-1 items-center justify-center gap-1.5 border-b-2 px-4 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        tab === value
          ? 'border-primary font-semibold text-foreground'
          : 'border-transparent font-medium text-muted-foreground hover:border-border hover:text-foreground'
      }`}
    >
      <Icon className="size-4" /> {label}
    </button>
  );

  const tabs = (
    <div className="flex flex-wrap items-center gap-2">
      <div role="tablist" className="flex w-56 border-b">
        {tabButton('form', 'Formulario', FileText)}
        {tabButton('receipt', 'Recibo', Receipt, receipts.length === 0)}
      </div>
      {receipts.length > 1 && tab === 'receipt' && (
        <div className="inline-flex items-center rounded-lg bg-muted p-1 text-sm">
          {receipts.map((r, i) => (
            <button
              key={r.public_token}
              type="button"
              onClick={() => setReceiptIdx(i)}
              className={`rounded-md px-2 py-1 ${i === receiptIdx ? 'bg-primary text-primary-foreground' : 'hover:bg-background/60'}`}
            >
              N° {formatReceiptNumber(r.number)}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const pdfButton = (
    <Button variant="outline" size="sm" onClick={startExport} disabled={exporting || !canExport}>
      {exporting ? <Loader2 className="mr-2 size-4 animate-spin" /> : <FileDown className="mr-2 size-4" />}
      Guardar PDF
    </Button>
  );

  const notice = reason && (
    <div
      role="alert"
      className="flex max-w-xl items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-left text-sm text-amber-900 print:hidden"
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <span>
        <strong>Recibo no disponible.</strong> {reason}
      </span>
    </div>
  );

  const offscreen = exporting && (
    <div aria-hidden className="pointer-events-none fixed print:hidden" style={{ left: '-10000px', top: 0 }}>
      <div ref={exportRef} style={{ width: '8.5in' }}>
        {sheet}
      </div>
    </div>
  );

  if (variant === 'portal') {
    return (
      <>
        <PrintPortal
          title={showingReceipt ? 'Recibo' : 'Formulario de registro'}
          pageSize="letter"
          onClose={onClose ?? (() => undefined)}
          toolbar={
            <>
              {tabs}
              {pdfButton}
            </>
          }
        >
          {notice}
          {exportError && <p className="text-sm text-red-700 print:hidden">No se pudo generar el PDF. Use Imprimir.</p>}
          <div data-print-page className="shadow-lg" style={{ zoom }}>
            {sheet}
          </div>
        </PrintPortal>
        {offscreen}
      </>
    );
  }

  return (
    <>
      <div className="mx-auto mb-4 flex max-w-3xl flex-col items-center gap-2 px-4 text-center print:hidden">
        {tabs}
        {notice}
        <div className="flex flex-wrap justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="mr-2 size-4" /> {showingReceipt ? 'Imprimir recibo' : 'Imprimir formulario'}
          </Button>
          {pdfButton}
        </div>
        {exportError && (
          <p className="text-sm text-red-700">No se pudo generar el PDF. Use Imprimir y elija «Guardar como PDF».</p>
        )}
      </div>

      <div className="flex justify-center px-4 print:block print:p-0">
        <div data-doc-zoom className="shadow-lg print:shadow-none" style={{ zoom }}>
          {sheet}
        </div>
      </div>
      {offscreen}

      <style>{`
        @media print {
          @page { size: letter; margin: 0; }
          body { background: white !important; }
          [data-doc-zoom] { zoom: 1 !important; }
        }
      `}</style>
    </>
  );
}
